import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  MdArrowBack, 
  MdEmail, 
  MdPhone, 
  MdLocationOn,
  MdPerson,
  MdEdit,
  MdDelete,
  MdVisibility,
  MdCalendarToday,
  MdAttachMoney,
  MdPayment,
  MdRepeat,
  MdCheckCircle,
  MdCancel,
  MdAdd
} from 'react-icons/md';
import { clientesAPI, recibosAPI, recibosRecurrentesAPI, configuracionAPI, actividadesAPI, usuariosAPI, aiAPI, gruposAPI } from '../services/api';
import Adjuntos from '../components/Adjuntos';
import CalendarioRecordatorios from '../components/CalendarioRecordatorios';
import { useModal } from '../context/ModalContext';
import { generateThermalReport, generateThermalEstadoCuenta } from '../utils/thermalPdfUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ClientProfileContainer = styled.div`
  width: 100%;
  max-width: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
`;

const BackButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.backgroundLight};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.textPrimary};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  svg {
    font-size: 1.5rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.backgroundLight};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const PrimaryButton = styled(Button)`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border-color: ${props => props.theme.colors.primary};

  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
    color: white;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.card};
`;

const CardTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const AICard = styled.div`
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}0d, ${p => p.theme.colors.primaryLight}0d);
  border: 1px solid ${p => p.theme.colors.primary}30;
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const AICardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing.md};
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const AICardTitle = styled.h3`
  margin: 0;
  font-size: ${p => p.theme.fontSizes.md};
  font-weight: 700;
  color: ${p => p.theme.colors.primary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AIGenBtn = styled.button`
  padding: 7px 16px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex;
  align-items: center;
  gap: 6px;
  transition: ${p => p.theme.transitions.fast};
  &:disabled { opacity: 0.6; }
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px ${p => p.theme.colors.primary}40; }
`;

const AIText = styled.p`
  margin: 0;
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textSecondary};
  line-height: 1.7;
  font-style: italic;
`;

const AIError = styled.p`
  margin: 0;
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.danger};
`;

const ClientInfoSection = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Avatar = styled.div`
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  border-radius: ${props => props.theme.borderRadius.round};
  overflow: hidden;
  flex-shrink: 0;
  border: 3px solid ${props => props.theme.colors.border};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ClientDetails = styled.div`
  flex: 1;
`;

const ClientName = styled.h3`
  font-size: ${props => props.theme.fontSizes.xxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.xs} 0;
  font-weight: ${props => props.theme.fontWeights.bold};
`;

const ClientEmail = styled.p`
  font-size: ${props => props.theme.fontSizes.md};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};

  svg {
    color: ${props => props.theme.colors.primary};
    font-size: 1.125rem;
  }
`;

const FinancialSummary = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const SummaryLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const SummaryValue = styled.span`
  font-size: ${props => props.theme.fontSizes.xxl};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.bold};
`;

const SummarySubtext = styled.span`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
`;

const RecurrentCard = styled.div`
  background: linear-gradient(to bottom right, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  }
`;

const RecurrentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  position: relative;
  z-index: 1;
  
  svg {
    font-size: 2rem;
    opacity: 0.9;
  }
  
  h3 {
    margin: 10px 0;
    font-size: ${props => props.theme.fontSizes.xl};
  }
`;

const RecurrentInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  position: relative;
  z-index: 1;
`;

const RecurrentInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const RecurrentLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.xs};
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RecurrentValue = styled.span`
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: ${props => props.theme.fontWeights.bold};
`;

const RecurrentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  backdrop-filter: blur(10px);
  
  svg {
    font-size: 1rem;
  }
`;

const NoRecurrentCard = styled.div`
  background: ${props => props.theme.colors.background};
  border: 2px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  
  svg {
    font-size: 3rem;
    opacity: 0.3;
    margin-bottom: ${props => props.theme.spacing.md};
  }
  
  p {
    margin: 0;
    font-size: ${props => props.theme.fontSizes.sm};
  }
`;

const RecurrentActions = styled.div`
  position: absolute;
  top: ${props => props.theme.spacing.lg};
  right: ${props => props.theme.spacing.lg};
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  z-index: 2;
`;

const RecurrentIconBtn = styled.button`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: none;
  border-radius: ${props => props.theme.borderRadius.sm};
  color: white;
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};

  svg {
    font-size: 1rem;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }
`;

const RecurrentSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};
`;

const MiniFormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const MiniLabel = styled.label`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: 600;
  display: block;
  margin-bottom: 4px;
`;

const DueDateBadge = styled.span`
  display: inline-block;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.semibold};
  background-color: ${props => props.theme.colors.danger}20;
  color: ${props => props.theme.colors.danger};
  margin-top: ${props => props.theme.spacing.xs};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 560px;
`;

const Thead = styled.thead`
  background-color: ${props => props.theme.colors.background};
`;

const Th = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.textSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  border-bottom: 1px solid ${props => props.theme.colors.borderLight};
`;

const StatusBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  background-color: ${props => {
    if (props.status === 'Pagado') return props.theme.colors.success + '20';
    if (props.status === 'Vencido') return props.theme.colors.danger + '20';
    return props.theme.colors.warning + '20';
  }};
  color: ${props => {
    if (props.status === 'Pagado') return props.theme.colors.success;
    if (props.status === 'Vencido') return props.theme.colors.danger;
    return props.theme.colors.warning;
  }};
`;

const ActionButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  transition: ${props => props.theme.transitions.fast};
  border: none;
  cursor: pointer;

  svg {
    font-size: 1rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
  }
`;

const RemindersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const ReminderItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
`;

const ReminderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  flex: 1;
`;

const ReminderIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.primary}15;
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.primary};

  svg {
    font-size: 1.25rem;
  }
`;

const ReminderText = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const ReminderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const ReminderStatus = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.semibold};
  background-color: ${props => props.active ? props.theme.colors.success + '20' : props.theme.colors.textLight + '20'};
  color: ${props => props.active ? props.theme.colors.success : props.theme.colors.textSecondary};
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};

  svg {
    font-size: 1rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const AddReminderButton = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.backgroundLight};
  color: ${props => props.theme.colors.primary};
  border: 2px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FullWidthCard = styled(Card)`
  grid-column: 1 / -1;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const TimelineSection = styled.div`
  margin-top: ${p => p.theme.spacing.xl};
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const TimelineTitle = styled.h2`
  font-size: ${p => p.theme.fontSizes.xl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
`;

const ActivityForm = styled.div`
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
  border: 1px solid ${p => p.theme.colors.border};
`;

const ActivityItem = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  padding: ${p => p.theme.spacing.md} 0;
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
  &:last-child { border-bottom: none; }
`;

const ActivityDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 5px;
  flex-shrink: 0;
  background: ${p => {
    const colors = { nota: '#3B82F6', llamada: '#10B981', reunion: '#8B5CF6', email: '#F59E0B', whatsapp: '#06B6D4', sistema: '#6B7280' };
    return colors[p.tipo] || '#6B7280';
  }};
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityMeta = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  align-items: center;
  margin-bottom: 4px;
`;

const ActivityTipo = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 600;
  text-transform: uppercase;
  color: ${p => p.theme.colors.textLight};
`;

const ActivityFecha = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
`;

const ActivityDesc = styled.p`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
  line-height: 1.5;
`;

const SmallSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  font-family: ${p => p.theme.fonts.primary};
  margin-bottom: ${p => p.theme.spacing.sm};
`;

const SmallTextarea = styled.textarea`
  width: 100%;
  padding: ${p => p.theme.spacing.sm};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.sm};
  font-family: ${p => p.theme.fonts.primary};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  resize: vertical;
  min-height: 80px;
  margin-bottom: ${p => p.theme.spacing.sm};
  box-sizing: border-box;
`;

const SmallInput = styled.input`
  padding: 8px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.sm};
  font-family: ${p => p.theme.fonts.primary};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  box-sizing: border-box;
  width: 100%;
`;

const SmallButton = styled.button`
  padding: 7px 16px;
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  border: none;
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  background: ${p => p.primary
    ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
    : p.theme.colors.backgroundDark};
  color: ${p => p.primary ? 'white' : p.theme.colors.textSecondary};
  margin-right: ${p => p.theme.spacing.sm};
  &:hover { opacity: 0.85; }
`;

const SubclienteCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const SubclienteInfo = styled.div`
  flex: 1;
  min-width: 160px;
`;

const SubclienteName = styled.div`
  font-size: ${p => p.theme.fontSizes.md};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
`;

const SubclienteMeta = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  margin-top: 2px;
`;

const SubclienteBalance = styled.div`
  font-size: ${p => p.theme.fontSizes.md};
  font-weight: 700;
  color: ${p => parseFloat(p.saldo) > 0 ? '#E57373' : '#4CAF50'};
  white-space: nowrap;
`;

const SubclienteActions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
`;

const ClientProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { confirm, toast } = useModal();
  
  const [client, setClient] = useState(null);
  const [recibos, setRecibos] = useState([]);

  /* ── AI resumen ── */
  const [aiResumen, setAiResumen] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');

  const handleGenerarResumen = async () => {
    setAiLoading(true);
    setAiError('');
    try {
      const res = await aiAPI.resumenCliente(id);
      setAiResumen(res.data?.data?.resumen || '');
    } catch (err) {
      setAiError(err?.message || 'Error al generar el resumen. Verifica que la API key esté configurada.');
    } finally {
      setAiLoading(false);
    }
  };
  const [recibosRecurrentes, setRecibosRecurrentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [actividades, setActividades] = useState([]);
  const [loadingActividades, setLoadingActividades] = useState(false);
  const [nuevaActividad, setNuevaActividad] = useState({ tipo: 'nota', descripcion: '' });
  const [showActividadForm, setShowActividadForm] = useState(false);
  const [savingActividad, setSavingActividad] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [reassigning, setReassigning] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [changingGrupo, setChangingGrupo] = useState(false);

  /* ── Sub-clientes ── */
  const defaultSubclienteForm = () => ({ nombre: '', telefono: '', email: '', direccion: '', notas: '' });
  const [subclientes, setSubclientes] = useState([]);
  const [subclienteFormOpen, setSubclienteFormOpen] = useState(false);
  const [subclienteForm, setSubclienteForm] = useState(defaultSubclienteForm());
  const [savingSubcliente, setSavingSubcliente] = useState(false);

  /* ── Cobros recurrentes ── */
  const defaultRecurrenteForm = () => ({
    frecuencia: 'Mensual',
    monto: '',
    descripcion: '',
    dia_semana: 'Lunes',
    dia_mes: '1',
    mes_anual: '1',
    dia_anual: '1',
    habilitado: true,
  });
  const [recurrenteFormOpen, setRecurrenteFormOpen] = useState(false);
  const [editingRecurrenteId, setEditingRecurrenteId] = useState(null);
  const [recurrenteForm, setRecurrenteForm] = useState(defaultRecurrenteForm());
  const [savingRecurrente, setSavingRecurrente] = useState(false);

  useEffect(() => {
    loadClientData();
    loadConfig();
    loadActividades();
    loadSubclientes();
    usuariosAPI.getAll().then(r => setUsuarios(r.data.data || [])).catch(() => {});
    gruposAPI.getAll().then(r => setGrupos(r.data.data || [])).catch(() => {});
  }, [id]);

  const loadActividades = async () => {
    if (!id) return;
    setLoadingActividades(true);
    try {
      const res = await actividadesAPI.getByCliente(id);
      setActividades(res.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingActividades(false);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await configuracionAPI.getConfiguracion();
      setConfig(response.data.data);
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    }
  };

  const loadClientData = async () => {
    try {
      setLoading(true);
      
      // Cargar datos del cliente
      const clienteResponse = await clientesAPI.getById(id);
      setClient(clienteResponse.data.data);
      
      // Cargar recibos del cliente
      const recibosResponse = await recibosAPI.getByCliente(id);
      setRecibos(recibosResponse.data.data || []);
      
      // Cargar recibos recurrentes del cliente
      try {
        const recurrentesResponse = await recibosRecurrentesAPI.getByCliente(id);
        setRecibosRecurrentes(recurrentesResponse.data.data || []);
      } catch (err) {
        console.log('No hay recibos recurrentes o error al cargar:', err);
        setRecibosRecurrentes([]);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error al cargar datos del cliente:', err);
      setError(err.message || 'Error al cargar datos del cliente');
    } finally {
      setLoading(false);
    }
  };

  const loadRecurrentes = async () => {
    try {
      const res = await recibosRecurrentesAPI.getByCliente(id);
      setRecibosRecurrentes(res.data.data || []);
    } catch (err) {
      console.error('Error al cargar cobros recurrentes:', err);
    }
  };

  const openNewRecurrente = () => {
    setRecurrenteForm(defaultRecurrenteForm());
    setEditingRecurrenteId(null);
    setRecurrenteFormOpen(true);
  };

  const openEditRecurrente = (r) => {
    setRecurrenteForm({
      frecuencia: r.frecuencia,
      monto: r.monto,
      descripcion: r.descripcion,
      dia_semana: r.dia_semana || 'Lunes',
      dia_mes: r.dia_mes || '1',
      mes_anual: r.mes_anual || '1',
      dia_anual: r.dia_anual || '1',
      habilitado: !!r.habilitado,
    });
    setEditingRecurrenteId(r.id);
    setRecurrenteFormOpen(true);
  };

  const closeRecurrenteForm = () => {
    setRecurrenteFormOpen(false);
    setEditingRecurrenteId(null);
  };

  const buildRecurrentePayload = () => {
    const payload = {
      habilitado: recurrenteForm.habilitado,
      frecuencia: recurrenteForm.frecuencia,
      monto: parseFloat(recurrenteForm.monto) || 0,
      descripcion: recurrenteForm.descripcion.trim() || 'Recibo recurrente',
      dia_semana: null,
      dia_mes: null,
      mes_anual: null,
      dia_anual: null,
    };
    if (recurrenteForm.frecuencia === 'Semanal') {
      payload.dia_semana = recurrenteForm.dia_semana;
    } else if (recurrenteForm.frecuencia === 'Mensual') {
      payload.dia_mes = parseInt(recurrenteForm.dia_mes);
    } else if (recurrenteForm.frecuencia === 'Anual') {
      payload.mes_anual = parseInt(recurrenteForm.mes_anual);
      payload.dia_anual = parseInt(recurrenteForm.dia_anual);
    }
    return payload;
  };

  const handleGuardarRecurrente = async () => {
    if (!recurrenteForm.monto || parseFloat(recurrenteForm.monto) <= 0) {
      toast('Ingresa un monto válido', 'error');
      return;
    }
    if (!recurrenteForm.descripcion.trim()) {
      toast('Ingresa una descripción', 'error');
      return;
    }
    setSavingRecurrente(true);
    try {
      const payload = buildRecurrentePayload();
      if (editingRecurrenteId) {
        await recibosRecurrentesAPI.update(editingRecurrenteId, payload);
        toast('Cobro recurrente actualizado', 'success');
      } else {
        await recibosRecurrentesAPI.create({ cliente_id: id, ...payload });
        toast('Cobro recurrente creado', 'success');
      }
      closeRecurrenteForm();
      loadRecurrentes();
    } catch (err) {
      console.error(err);
      toast('Error al guardar el cobro recurrente: ' + (err.message || 'Error desconocido'), 'error');
    } finally {
      setSavingRecurrente(false);
    }
  };

  const handleEliminarRecurrente = async (r) => {
    const ok = await confirm(`¿Eliminar el cobro recurrente "${r.descripcion}"? Esta acción no se puede deshacer.`, {
      title: 'Eliminar cobro recurrente',
      confirmText: 'Eliminar',
      danger: true,
    });
    if (!ok) return;
    try {
      await recibosRecurrentesAPI.delete(r.id);
      toast('Cobro recurrente eliminado', 'success');
      if (editingRecurrenteId === r.id) closeRecurrenteForm();
      loadRecurrentes();
    } catch (err) {
      toast('Error al eliminar: ' + (err.message || 'Error desconocido'), 'error');
    }
  };

  const handleToggleHabilitadoRecurrente = async (r) => {
    try {
      await recibosRecurrentesAPI.update(r.id, {
        habilitado: !r.habilitado,
        frecuencia: r.frecuencia,
        monto: parseFloat(r.monto),
        descripcion: r.descripcion,
        dia_semana: r.dia_semana || null,
        dia_mes: r.dia_mes || null,
        mes_anual: r.mes_anual || null,
        dia_anual: r.dia_anual || null,
      });
      loadRecurrentes();
    } catch (err) {
      toast('Error al actualizar: ' + (err.message || 'Error desconocido'), 'error');
    }
  };

  /* ── Sub-clientes ── */
  const loadSubclientes = async () => {
    if (!id) return;
    try {
      const res = await clientesAPI.getSubclientes(id);
      setSubclientes(res.data.data || []);
    } catch (err) {
      console.error('Error al cargar sub-clientes:', err);
    }
  };

  const handleSepararSubcliente = async (sc) => {
    const ok = await confirm(
      `"${sc.nombre}" dejará de ser sub-cliente y pasará a ser un cliente independiente, con sus propios recibos y pagos. ¿Continuar?`,
      { title: 'Separar sub-cliente', confirmText: 'Separar' }
    );
    if (!ok) return;
    try {
      await clientesAPI.separarSubcliente(sc.id);
      toast('Sub-cliente separado exitosamente', 'success');
      loadSubclientes();
      loadClientData();
    } catch (err) {
      toast('Error al separar el sub-cliente: ' + (err.response?.data?.message || err.message || 'Error desconocido'), 'error');
    }
  };

  const openNewSubcliente = () => {
    setSubclienteForm(defaultSubclienteForm());
    setSubclienteFormOpen(true);
  };

  const closeSubclienteForm = () => {
    setSubclienteFormOpen(false);
  };

  // Solo crea sub-clientes nuevos — editar/eliminar uno existente se hace
  // desde su propia ficha completa (botón "Ver Perfil" en su tarjeta).
  const handleGuardarSubcliente = async () => {
    if (!subclienteForm.nombre.trim()) {
      toast('Ingresa un nombre para el sub-cliente', 'error');
      return;
    }
    setSavingSubcliente(true);
    try {
      const payload = {
        nombre: subclienteForm.nombre.trim(),
        telefono: subclienteForm.telefono.trim(),
        email: subclienteForm.email.trim(),
        direccion: subclienteForm.direccion.trim(),
        notas: subclienteForm.notas.trim(),
      };
      await clientesAPI.createSubcliente(id, payload);
      toast('Sub-cliente creado', 'success');
      closeSubclienteForm();
      loadSubclientes();
      loadClientData();
    } catch (err) {
      toast('Error al guardar el sub-cliente: ' + (err.response?.data?.message || err.message || 'Error desconocido'), 'error');
    } finally {
      setSavingSubcliente(false);
    }
  };

  const handleCrearActividad = async () => {
    if (!nuevaActividad.descripcion.trim()) return;
    setSavingActividad(true);
    try {
      await actividadesAPI.create({ cliente_id: id, ...nuevaActividad });
      setNuevaActividad({ tipo: 'nota', descripcion: '' });
      setShowActividadForm(false);
      loadActividades();
    } catch (e) {
      console.error(e);
    } finally {
      setSavingActividad(false);
    }
  };

  const handleReasignar = async (nuevoUsuarioId) => {
    setReassigning(true);
    try {
      await clientesAPI.update(id, { asignado_a: nuevoUsuarioId || null });
      const res = await clientesAPI.getById(id);
      setClient(res.data.data || res.data);
    } catch (err) {
      console.error('Error al reasignar:', err);
    } finally {
      setReassigning(false);
    }
  };

  const handleCambiarGrupo = async (nuevoGrupoId) => {
    setChangingGrupo(true);
    try {
      await clientesAPI.update(id, { grupo_id: nuevoGrupoId || null });
      const res = await clientesAPI.getById(id);
      setClient(res.data.data || res.data);
    } catch (err) {
      console.error('Error al cambiar de grupo:', err);
      toast('Error al cambiar de grupo: ' + (err.message || 'Error desconocido'), 'error');
    } finally {
      setChangingGrupo(false);
    }
  };

  const handleGeneratePDF = async () => {
    try {
      // Obtener recibos con desglose primero (se usa tanto en térmico como en A4)
      let recibosDesglosePre = [];
      try {
        const desgloseResponsePre = await recibosAPI.getByClienteDesglose(id);
        recibosDesglosePre = desgloseResponsePre.data.data || [];
      } catch (e) {
        recibosDesglosePre = recibos.map(r => ({ ...r, items: [], pagos: [] }));
      }

      // Formato térmico
      const useThermal = config?.formato_impresion === 'termica';
      if (useThermal) {
        const paperWidth = parseInt(config?.ancho_papel_termico) || 80;
        generateThermalEstadoCuenta(client, recibosDesglosePre, config, paperWidth, false);
        return;
      }

      // Formato normal A4
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPosition = 12;
      
      // Configuración de la empresa
      const empresa = config?.nombre_empresa || 'Mi Empresa';
      const direccion = config?.direccion || '';
      const telefono = config?.telefono || '';
      const email = config?.email_contacto || '';
      const logoUrl = config?.logo_url || '';

      // Encabezado con logo y nombre de empresa
      const headerStartY = yPosition;
      
      // Agregar logo si existe (lado izquierdo)
      if (logoUrl && logoUrl.startsWith('data:image')) {
        try {
          const logoSize = 20;
          const logoX = 20;
          doc.addImage(logoUrl, 'PNG', logoX, yPosition, logoSize, logoSize);
        } catch (error) {
          console.error('Error al agregar logo:', error);
        }
      }

      // Información de la empresa (lado derecho del logo o centrado si no hay logo)
      const textX = logoUrl && logoUrl.startsWith('data:image') ? 45 : pageWidth / 2;
      const textAlign = logoUrl && logoUrl.startsWith('data:image') ? 'left' : 'center';
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(empresa, textX, yPosition + 5, { align: textAlign });
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      let infoY = yPosition + 10;
      
      if (direccion) {
        doc.text(direccion, textX, infoY, { align: textAlign });
        infoY += 4;
      }
      if (telefono || email) {
        const contacto = [telefono, email].filter(Boolean).join(' | ');
        doc.text(contacto, textX, infoY, { align: textAlign });
        infoY += 4;
      }
      
      yPosition = Math.max(headerStartY + 20, infoY);

      // Línea separadora
      yPosition += 3;
      doc.setDrawColor(200, 200, 200);
      doc.line(15, yPosition, pageWidth - 15, yPosition);
      yPosition += 8;
      
      // Título del documento
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(91, 155, 213);
      doc.text('ESTADO DE CUENTA', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      doc.setTextColor(0, 0, 0);
      
      // Información del cliente en contenedor gris
      const containerMargin = 15;
      const containerWidth = pageWidth - (containerMargin * 2);
      
      doc.setFillColor(245, 245, 245);
      doc.rect(containerMargin, yPosition - 3, containerWidth, 32, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('CLIENTE', containerMargin + 5, yPosition);
      yPosition += 6;
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(client?.nombre || 'N/A', containerMargin + 5, yPosition);
      yPosition += 5;
      
      doc.setFontSize(9);
      let clienteInfo = '';
      if (client?.rfc) {
        clienteInfo = `RFC: ${client.rfc}`;
      }
      if (client?.telefono) {
        clienteInfo += (clienteInfo ? ' • ' : '') + `Tel: ${client.telefono}`;
      }
      if (client?.email) {
        clienteInfo += (clienteInfo ? ' • ' : '') + client.email;
      }
      
      if (clienteInfo) {
        const maxWidth = containerWidth - 10;
        const infoLines = doc.splitTextToSize(clienteInfo, maxWidth);
        doc.text(infoLines, containerMargin + 5, yPosition);
        yPosition += (infoLines.length * 4);
      }
      yPosition += 8;
      
      // Fecha de generación
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 100, 100);
      const fechaActual = new Date().toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      doc.text(`Fecha de generación: ${fechaActual}`, containerMargin + 5, yPosition);
      yPosition += 8;
      doc.setTextColor(0, 0, 0);
      
      // Usar el desglose ya cargado al inicio de la función
      const recibosDesglose = recibosDesglosePre;

      // Calcular resumen
      const totalRecibos = recibosDesglose.length;
      const totalMonto = recibosDesglose.reduce((sum, r) => sum + parseFloat(r.monto_total || 0), 0);
      const totalPagado = recibosDesglose.reduce((sum, r) => sum + (parseFloat(r.monto_total || 0) - parseFloat(r.saldo_pendiente || 0)), 0);
      const totalPendiente = recibosDesglose.reduce((sum, r) => sum + parseFloat(r.saldo_pendiente || 0), 0);

      // Encabezado de resumen
      doc.setFillColor(240, 248, 255);
      doc.rect(containerMargin, yPosition - 2, containerWidth, 8, 'F');

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(91, 155, 213);
      doc.text('RESUMEN FINANCIERO', containerMargin + 5, yPosition + 3);
      doc.setTextColor(0, 0, 0);
      yPosition += 10;

      autoTable(doc, {
        startY: yPosition,
        head: [['Concepto', 'Monto']],
        body: [
          ['Total de Recibos', totalRecibos.toString()],
          ['Monto Total', `$${totalMonto.toFixed(2)}`],
          ['Total Pagado', `$${totalPagado.toFixed(2)}`],
          ['Saldo Pendiente', `$${totalPendiente.toFixed(2)}`]
        ],
        theme: 'striped',
        headStyles: { fillColor: [91, 155, 213], textColor: 255, fontStyle: 'bold', fontSize: 10 },
        styles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 120, halign: 'left' },
          1: { cellWidth: 60, halign: 'right', fontStyle: 'bold' }
        },
        margin: { left: containerMargin, right: containerMargin }
      });

      // Sección de detalle desglosado por recibo
      yPosition = doc.lastAutoTable.finalY + 12;

      doc.setFillColor(240, 248, 255);
      doc.rect(containerMargin, yPosition - 2, containerWidth, 8, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(91, 155, 213);
      doc.text('DETALLE DE RECIBOS', containerMargin + 5, yPosition + 3);
      doc.setTextColor(0, 0, 0);
      yPosition += 12;

      const estadoLabel = e =>
        e === 'pagado' ? 'Pagado' :
        e === 'pendiente' ? 'Pendiente' :
        e === 'vencido' ? 'Vencido' :
        e === 'parcial' ? 'Parcial' : (e || '');

      const estadoColor = e =>
        e === 'Pagado' ? [22, 163, 74] :
        e === 'Vencido' ? [220, 38, 38] :
        e === 'Parcial' ? [234, 88, 12] :
        [100, 116, 139];

      for (const recibo of recibosDesglose) {
        const pageHeight = doc.internal.pageSize.getHeight();
        if (yPosition > pageHeight - 50) {
          doc.addPage();
          yPosition = 15;
        }

        const estado = estadoLabel(recibo.estado);
        const color = estadoColor(estado);

        // Cabecera del recibo
        doc.setFillColor(248, 250, 252);
        doc.rect(containerMargin, yPosition - 2, containerWidth, 9, 'F');
        doc.setDrawColor(200, 200, 200);
        doc.rect(containerMargin, yPosition - 2, containerWidth, 9, 'S');

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 30, 30);
        doc.text(`Recibo: ${recibo.numero_recibo || 'N/A'}`, containerMargin + 3, yPosition + 4);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        const emision = new Date(recibo.fecha_emision).toLocaleDateString('es-MX');
        const vencimiento = recibo.fecha_vencimiento
          ? new Date(recibo.fecha_vencimiento).toLocaleDateString('es-MX')
          : 'N/A';
        doc.text(`Emisión: ${emision}   Vencimiento: ${vencimiento}`, containerMargin + 60, yPosition + 4);

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...color);
        doc.text(estado, containerMargin + containerWidth - 25, yPosition + 4, { align: 'right' });
        doc.setTextColor(30, 30, 30);
        yPosition += 10;

        // Items del recibo
        if (recibo.items && recibo.items.length > 0) {
          const itemsBody = recibo.items.map(item => [
            item.descripcion || '',
            item.cantidad?.toString() || '1',
            `$${parseFloat(item.precio_unitario || 0).toFixed(2)}`,
            `$${parseFloat(item.subtotal || 0).toFixed(2)}`
          ]);

          autoTable(doc, {
            startY: yPosition,
            head: [['Descripción', 'Cant.', 'Precio Unit.', 'Subtotal']],
            body: itemsBody,
            theme: 'plain',
            headStyles: {
              fillColor: [226, 232, 240],
              textColor: [50, 50, 50],
              fontStyle: 'bold',
              fontSize: 8,
            },
            styles: { fontSize: 8 },
            columnStyles: {
              0: { halign: 'left' },
              1: { cellWidth: 18, halign: 'center' },
              2: { cellWidth: 30, halign: 'right' },
              3: { cellWidth: 30, halign: 'right' }
            },
            margin: { left: containerMargin + 4, right: containerMargin + 4 }
          });
          yPosition = doc.lastAutoTable.finalY + 2;
        } else if (recibo.descripcion) {
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(80, 80, 80);
          doc.text(recibo.descripcion, containerMargin + 5, yPosition + 3);
          yPosition += 7;
          doc.setTextColor(0, 0, 0);
        }

        // Totales del recibo
        const subtotal = parseFloat(recibo.subtotal || recibo.monto_total || 0);
        const montoIva = parseFloat(recibo.monto_iva || 0);
        const montoDescuento = parseFloat(recibo.monto_descuento || 0);
        const montoTotal = parseFloat(recibo.monto_total || 0);
        const saldo = parseFloat(recibo.saldo_pendiente || 0);
        const pagadoRecibo = montoTotal - saldo;

        const totalesBody = [];
        if (montoDescuento > 0) totalesBody.push(['Descuento:', `-$${montoDescuento.toFixed(2)}`]);
        if (montoIva > 0) totalesBody.push([`IVA (${recibo.iva || 16}%):`, `$${montoIva.toFixed(2)}`]);
        totalesBody.push(['Total:', `$${montoTotal.toFixed(2)}`]);
        if (pagadoRecibo > 0) totalesBody.push(['Pagado:', `$${pagadoRecibo.toFixed(2)}`]);
        totalesBody.push(['Saldo pendiente:', `$${saldo.toFixed(2)}`]);

        autoTable(doc, {
          startY: yPosition,
          body: totalesBody,
          theme: 'plain',
          styles: { fontSize: 8 },
          columnStyles: {
            0: { halign: 'right', fontStyle: 'bold', cellWidth: 140 },
            1: { halign: 'right', cellWidth: 30 }
          },
          margin: { left: containerMargin + 4, right: containerMargin + 4 }
        });
        yPosition = doc.lastAutoTable.finalY + 2;

        // Pagos aplicados al recibo
        if (recibo.pagos && recibo.pagos.length > 0) {
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(80, 80, 80);
          doc.text('Pagos registrados:', containerMargin + 5, yPosition + 3);
          yPosition += 5;

          const pagosBody = recibo.pagos.map(p => [
            new Date(p.fecha_pago).toLocaleDateString('es-MX'),
            p.metodo_pago || '',
            p.referencia || '',
            `$${parseFloat(p.monto || 0).toFixed(2)}`
          ]);

          autoTable(doc, {
            startY: yPosition,
            head: [['Fecha', 'Método', 'Referencia', 'Monto']],
            body: pagosBody,
            theme: 'plain',
            headStyles: {
              fillColor: [220, 252, 231],
              textColor: [22, 101, 52],
              fontStyle: 'bold',
              fontSize: 7.5
            },
            styles: { fontSize: 7.5 },
            columnStyles: {
              0: { cellWidth: 24, halign: 'center' },
              1: { cellWidth: 40, halign: 'left' },
              2: { halign: 'left' },
              3: { cellWidth: 28, halign: 'right' }
            },
            margin: { left: containerMargin + 4, right: containerMargin + 4 }
          });
          yPosition = doc.lastAutoTable.finalY + 2;
          doc.setTextColor(0, 0, 0);
        }

        yPosition += 6;
      }
      
      
      // Pie de página
      const pageCount = doc.internal.getNumberOfPages();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(100, 100, 100);
        
        // Texto del pie de página
        doc.text(
          'Estado de cuenta generado automáticamente',
          pageWidth / 2,
          pageHeight - 15,
          { align: 'center' }
        );
        
        // Número de página
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Página ${i} de ${pageCount}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }
      
      // Guardar PDF
      const clientNameClean = client?.nombre?.replace(/\s/g, '_') || 'Cliente';
      doc.save(`Estado_Cuenta_${clientNameClean}_${new Date().getTime()}.pdf`);
      
    } catch (error) {
      console.error('Error al generar PDF:', error);
      toast('Error al generar el PDF: ' + error.message, 'error');
    }
  };

  const handleSendEmail = () => {
    toast('Enviando recibo por email...', 'info');
  };

  const handleScheduleReminders = () => {
    toast('Programando recordatorios...', 'info');
  };

  const handleDeleteClient = async () => {
    const ok = await confirm('¿Enviar este cliente a la papelera? Podrás restaurarlo después desde Configuración → Importar Clientes.', {
      title: 'Eliminar Cliente',
      confirmText: 'Enviar a la papelera',
      danger: true,
    });
    if (ok) {
      try {
        await clientesAPI.delete(id);
        toast('Cliente enviado a la papelera', 'success');
        navigate('/clientes');
      } catch (error) {
        toast('Error al eliminar cliente: ' + (error.message || 'Error desconocido'), 'error');
      }
    }
  };

  const getEstadoBadge = (estado) => {
    const estadoLower = estado?.toLowerCase();
    return {
      color: estadoLower === 'pagado' ? '#10b981' : 
             estadoLower === 'vencido' ? '#ef4444' : 
             estadoLower === 'parcial' ? '#3b82f6' : '#f59e0b',
      text: estado?.charAt(0).toUpperCase() + estado?.slice(1).toLowerCase()
    };
  };

  if (loading) {
    return (
      <ClientProfileContainer>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <p>Cargando datos del cliente...</p>
        </div>
      </ClientProfileContainer>
    );
  }

  if (error || !client) {
    return (
      <ClientProfileContainer>
        <div style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
          <p>Error: {error || 'Cliente no encontrado'}</p>
          <Button onClick={() => navigate('/clientes')}>
            Volver a Clientes
          </Button>
        </div>
      </ClientProfileContainer>
    );
  }

  return (
    <ClientProfileContainer>
      <PageHeader>
        <HeaderLeft>
          <BackButton onClick={() => navigate(-1)}>
            <MdArrowBack />
          </BackButton>
          <div>
            <Title>Ficha de Cliente: {client.nombre}</Title>
            {client.padre_cliente_id && (
              <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                Sub-cliente de{' '}
                <button
                  onClick={() => navigate(`/clientes/${client.padre_cliente_id}`)}
                  style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}
                >
                  {client.padre_nombre}
                </button>
                {' '}— sus cobros se consolidan ahí.
              </div>
            )}
          </div>
        </HeaderLeft>
        
        <ActionButtons>
          <Button onClick={() => navigate('/recibos/nuevo', { state: { clienteId: client.id } })}>
            <MdAttachMoney />
            Nuevo Recibo
          </Button>
          <Button onClick={() => navigate('/registrar-pago', { state: { clienteId: client.id } })}>
            <MdPayment />
            Registrar Pago
          </Button>
          <PrimaryButton onClick={() => navigate(`/clientes/${client.id}/editar`)}>
            <MdEdit />
            Editar Cliente
          </PrimaryButton>
        </ActionButtons>
      </PageHeader>

      <ContentGrid>
        <Card>
          <CardTitle>Información de Contacto</CardTitle>
          
          <ClientInfoSection>
            <Avatar>
              {client.foto_url ? (
                <img src={client.foto_url} alt={client.nombre} />
              ) : (
                <MdPerson style={{ fontSize: '3rem', color: '#9ca3af' }} />
              )}
            </Avatar>
            
            <ClientDetails>
              <ClientName>{client.nombre}</ClientName>
              <ClientEmail>{client.email || 'Sin email'}</ClientEmail>
              
              <ContactInfo>
                {client.telefono && (
                  <ContactItem>
                    <MdPhone />
                    <span>{client.telefono}</span>
                  </ContactItem>
                )}
                {client.direccion && (
                  <ContactItem>
                    <MdLocationOn />
                    <span>{client.direccion}</span>
                  </ContactItem>
                )}
                {client.rfc && (
                  <ContactItem>
                    <MdPerson />
                    <span>RFC: {client.rfc}</span>
                  </ContactItem>
                )}
                {client.regimen_fiscal && (
                  <ContactItem>
                    <MdPerson />
                    <span>Régimen Fiscal: {client.regimen_fiscal}</span>
                  </ContactItem>
                )}
              </ContactInfo>
            </ClientDetails>
          </ClientInfoSection>

          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid', borderColor: 'inherit', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Responsable</span>
            <select
              value={client.asignado_a || ''}
              onChange={e => handleReasignar(e.target.value)}
              disabled={reassigning}
              style={{
                padding: '4px 10px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '0.875rem',
                background: 'transparent',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <option value="">Sin asignar</option>
              {usuarios.map(u => (
                <option key={u.id} value={u.id}>{u.nombre}</option>
              ))}
            </select>
          </div>

          <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Grupo</span>
            <select
              value={client.grupo_id || ''}
              onChange={e => handleCambiarGrupo(e.target.value)}
              disabled={changingGrupo}
              style={{
                padding: '4px 10px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '0.875rem',
                background: 'transparent',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <option value="">Sin grupo</option>
              {grupos.map(g => (
                <option key={g.id} value={g.id}>{g.nombre}</option>
              ))}
            </select>
            {client.grupo_id && (
              <SmallButton onClick={() => navigate(`/grupos/${client.grupo_id}`)} style={{ marginRight: 0 }}>
                Ver grupo
              </SmallButton>
            )}
          </div>
        </Card>

        <Card>
          <CardTitle>Resumen Financiero</CardTitle>
          
          <FinancialSummary>
            <SummaryItem>
              <SummaryLabel>{parseInt(client.total_subclientes || 0) > 0 ? 'Saldo Propio' : 'Saldo Pendiente'}</SummaryLabel>
              <SummaryValue style={{ color: parseFloat(client.saldo_pendiente || 0) > 0 ? '#E57373' : '#4CAF50' }}>
                ${parseFloat(client.saldo_pendiente || 0).toFixed(2)}
              </SummaryValue>
            </SummaryItem>

            {parseInt(client.total_subclientes || 0) > 0 && (
              <>
                <SummaryItem>
                  <SummaryLabel>Saldo de Sub-clientes</SummaryLabel>
                  <SummaryValue style={{ color: parseFloat(client.saldo_subclientes || 0) > 0 ? '#E57373' : '#4CAF50' }}>
                    ${parseFloat(client.saldo_subclientes || 0).toFixed(2)}
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Saldo Total</SummaryLabel>
                  <SummaryValue style={{ color: parseFloat(client.saldo_total || 0) > 0 ? '#E57373' : '#4CAF50' }}>
                    ${parseFloat(client.saldo_total || 0).toFixed(2)}
                  </SummaryValue>
                </SummaryItem>
              </>
            )}

            <SummaryItem>
              <SummaryLabel>Total de Recibos</SummaryLabel>
              <SummaryValue style={{ fontSize: '1.5rem', color: '#3b82f6' }}>
                {recibos.length}
              </SummaryValue>
              <SummarySubtext>
                {recibos.filter(r => r.estado === 'vencido').length} vencidos
              </SummarySubtext>
            </SummaryItem>

            <SummaryItem>
              <SummaryLabel>Fecha de Registro</SummaryLabel>
              <SummaryValue style={{ fontSize: '1.125rem' }}>
                <MdCalendarToday style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: '4px' }} />
                {new Date(client.fecha_registro).toLocaleDateString('es-MX')}
              </SummaryValue>
            </SummaryItem>
          </FinancialSummary>
        </Card>
      </ContentGrid>

      {/* ── Panel de resumen IA ── */}
      <AICard>
        <AICardHeader>
          <AICardTitle>✨ Resumen inteligente</AICardTitle>
          <AIGenBtn onClick={handleGenerarResumen} disabled={aiLoading}>
            {aiLoading ? 'Analizando...' : aiResumen ? '↻ Regenerar' : '✨ Generar análisis'}
          </AIGenBtn>
        </AICardHeader>
        {aiError && <AIError>{aiError}</AIError>}
        {aiResumen
          ? <AIText>{aiResumen}</AIText>
          : !aiError && <AIText style={{ opacity: 0.5 }}>Haz clic en "Generar análisis" para obtener un resumen inteligente del comportamiento y estado de este cliente.</AIText>
        }
      </AICard>

      {/* Sección de Sub-clientes: viven dentro de este cliente, no aparecen
          sueltos en el listado principal. Sus cobros recaen en este cliente
          (saldo consolidado arriba), pero se distinguen por nombre. Un
          sub-cliente no puede tener sus propios sub-clientes, así que esta
          sección no se muestra al ver la ficha de un sub-cliente. */}
      {!client.padre_cliente_id && (
      <div style={{ marginBottom: '24px' }}>
        <RecurrentSectionHeader>
          <CardTitle style={{ margin: 0 }}>
            Sub-clientes{subclientes.length > 0 && ` (${subclientes.length})`}
          </CardTitle>
          {!subclienteFormOpen && (
            <SmallButton primary onClick={openNewSubcliente}>
              <MdAdd style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              Nuevo sub-cliente
            </SmallButton>
          )}
        </RecurrentSectionHeader>

        {subclienteFormOpen && (
          <ActivityForm>
            <MiniFormGrid>
              <div>
                <MiniLabel>Nombre</MiniLabel>
                <SmallInput
                  type="text"
                  value={subclienteForm.nombre}
                  onChange={e => setSubclienteForm(f => ({ ...f, nombre: e.target.value }))}
                  placeholder="Nombre del sub-cliente"
                  autoFocus
                />
              </div>
              <div>
                <MiniLabel>Teléfono</MiniLabel>
                <SmallInput
                  type="text"
                  value={subclienteForm.telefono}
                  onChange={e => setSubclienteForm(f => ({ ...f, telefono: e.target.value }))}
                  placeholder="Teléfono"
                />
              </div>
              <div>
                <MiniLabel>Email</MiniLabel>
                <SmallInput
                  type="email"
                  value={subclienteForm.email}
                  onChange={e => setSubclienteForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="Email"
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <MiniLabel>Dirección</MiniLabel>
                <SmallInput
                  type="text"
                  value={subclienteForm.direccion}
                  onChange={e => setSubclienteForm(f => ({ ...f, direccion: e.target.value }))}
                  placeholder="Dirección"
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <MiniLabel>Notas</MiniLabel>
                <SmallInput
                  type="text"
                  value={subclienteForm.notas}
                  onChange={e => setSubclienteForm(f => ({ ...f, notas: e.target.value }))}
                  placeholder="Notas (opcional)"
                />
              </div>
            </MiniFormGrid>

            <div>
              <SmallButton primary onClick={handleGuardarSubcliente} disabled={savingSubcliente}>
                {savingSubcliente ? 'Guardando...' : 'Guardar'}
              </SmallButton>
              <SmallButton onClick={closeSubclienteForm}>Cancelar</SmallButton>
            </div>
          </ActivityForm>
        )}

        {subclientes.length > 0 ? (
          subclientes.map(sc => (
            <SubclienteCard key={sc.id}>
              <SubclienteInfo>
                <SubclienteName
                  as="button"
                  onClick={() => navigate(`/clientes/${sc.id}`)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                  title="Ver ficha completa"
                >
                  {sc.nombre}
                </SubclienteName>
                <SubclienteMeta>
                  {sc.telefono || sc.email || 'Sin contacto'}
                  {sc.total_recibos > 0 ? ` · ${sc.total_recibos} recibo${sc.total_recibos === 1 ? '' : 's'}` : ''}
                </SubclienteMeta>
              </SubclienteInfo>
              <SubclienteBalance saldo={sc.saldo_pendiente}>
                ${parseFloat(sc.saldo_pendiente || 0).toFixed(2)}
              </SubclienteBalance>
              <SubclienteActions>
                <SmallButton onClick={() => navigate(`/clientes/${sc.id}`)} style={{ marginRight: 0 }}>
                  Ver Perfil
                </SmallButton>
                <SmallButton onClick={() => navigate('/recibos/nuevo', { state: { clienteId: sc.id } })} style={{ marginRight: 0 }}>
                  Nuevo Recibo
                </SmallButton>
                <SmallButton onClick={() => navigate('/registrar-pago', { state: { clienteId: sc.id } })} style={{ marginRight: 0 }}>
                  Registrar Pago
                </SmallButton>
                <SmallButton onClick={() => handleSepararSubcliente(sc)} style={{ marginRight: 0 }} title="Convertir en cliente independiente">
                  Separar
                </SmallButton>
              </SubclienteActions>
            </SubclienteCard>
          ))
        ) : (
          !subclienteFormOpen && (
            <NoRecurrentCard>
              <MdPerson />
              <p>Este cliente no tiene sub-clientes. Agrega uno arriba para distinguir sus cobros dentro de este cliente.</p>
            </NoRecurrentCard>
          )
        )}
      </div>
      )}

      {/* Sección de Cobros Recurrentes */}
      <div style={{ marginBottom: '24px' }}>
        <RecurrentSectionHeader>
          <CardTitle style={{ margin: 0 }}>
            Cobros Recurrentes{recibosRecurrentes.length > 0 && ` (${recibosRecurrentes.length})`}
          </CardTitle>
          {!recurrenteFormOpen && (
            <SmallButton primary onClick={openNewRecurrente}>
              <MdAdd style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              Nuevo cobro recurrente
            </SmallButton>
          )}
        </RecurrentSectionHeader>

        {recurrenteFormOpen && (
          <ActivityForm>
            <MiniFormGrid>
              <div>
                <MiniLabel>Frecuencia</MiniLabel>
                <SmallSelect
                  style={{ width: '100%', marginBottom: 0 }}
                  value={recurrenteForm.frecuencia}
                  onChange={e => setRecurrenteForm(f => ({ ...f, frecuencia: e.target.value }))}
                >
                  <option value="Diario">Diario</option>
                  <option value="Semanal">Semanal</option>
                  <option value="Mensual">Mensual</option>
                  <option value="Anual">Anual</option>
                </SmallSelect>
              </div>

              {recurrenteForm.frecuencia === 'Semanal' && (
                <div>
                  <MiniLabel>Día de la semana</MiniLabel>
                  <SmallSelect
                    style={{ width: '100%', marginBottom: 0 }}
                    value={recurrenteForm.dia_semana}
                    onChange={e => setRecurrenteForm(f => ({ ...f, dia_semana: e.target.value }))}
                  >
                    <option value="Lunes">Lunes</option>
                    <option value="Martes">Martes</option>
                    <option value="Miércoles">Miércoles</option>
                    <option value="Jueves">Jueves</option>
                    <option value="Viernes">Viernes</option>
                    <option value="Sábado">Sábado</option>
                    <option value="Domingo">Domingo</option>
                  </SmallSelect>
                </div>
              )}

              {recurrenteForm.frecuencia === 'Mensual' && (
                <div>
                  <MiniLabel>Día del mes</MiniLabel>
                  <SmallSelect
                    style={{ width: '100%', marginBottom: 0 }}
                    value={recurrenteForm.dia_mes}
                    onChange={e => setRecurrenteForm(f => ({ ...f, dia_mes: e.target.value }))}
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </SmallSelect>
                </div>
              )}

              {recurrenteForm.frecuencia === 'Anual' && (
                <>
                  <div>
                    <MiniLabel>Mes</MiniLabel>
                    <SmallSelect
                      style={{ width: '100%', marginBottom: 0 }}
                      value={recurrenteForm.mes_anual}
                      onChange={e => setRecurrenteForm(f => ({ ...f, mes_anual: e.target.value }))}
                    >
                      {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'].map((m, idx) => (
                        <option key={m} value={idx + 1}>{m}</option>
                      ))}
                    </SmallSelect>
                  </div>
                  <div>
                    <MiniLabel>Día</MiniLabel>
                    <SmallSelect
                      style={{ width: '100%', marginBottom: 0 }}
                      value={recurrenteForm.dia_anual}
                      onChange={e => setRecurrenteForm(f => ({ ...f, dia_anual: e.target.value }))}
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </SmallSelect>
                  </div>
                </>
              )}

              <div>
                <MiniLabel>Monto ($)</MiniLabel>
                <SmallInput
                  type="number"
                  step="0.01"
                  min="0"
                  value={recurrenteForm.monto}
                  onChange={e => setRecurrenteForm(f => ({ ...f, monto: e.target.value }))}
                  placeholder="0.00"
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <MiniLabel>Descripción</MiniLabel>
                <SmallInput
                  type="text"
                  value={recurrenteForm.descripcion}
                  onChange={e => setRecurrenteForm(f => ({ ...f, descripcion: e.target.value }))}
                  placeholder="Ej: Renta mensual, Servicio de mantenimiento..."
                />
              </div>
            </MiniFormGrid>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', marginBottom: '12px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={recurrenteForm.habilitado}
                onChange={e => setRecurrenteForm(f => ({ ...f, habilitado: e.target.checked }))}
              />
              Habilitado
            </label>

            <div>
              <SmallButton primary onClick={handleGuardarRecurrente} disabled={savingRecurrente}>
                {savingRecurrente ? 'Guardando...' : editingRecurrenteId ? 'Actualizar' : 'Guardar'}
              </SmallButton>
              <SmallButton onClick={closeRecurrenteForm}>Cancelar</SmallButton>
            </div>
          </ActivityForm>
        )}

        {recibosRecurrentes.length > 0 ? (
          recibosRecurrentes.map((recurrente) => (
            <RecurrentCard key={recurrente.id} style={{ marginBottom: '16px', opacity: recurrente.habilitado ? 1 : 0.6 }}>
              <RecurrentActions>
                <RecurrentIconBtn
                  onClick={() => handleToggleHabilitadoRecurrente(recurrente)}
                  title={recurrente.habilitado ? 'Deshabilitar' : 'Habilitar'}
                >
                  {recurrente.habilitado ? <MdCheckCircle /> : <MdCancel />}
                </RecurrentIconBtn>
                <RecurrentIconBtn onClick={() => openEditRecurrente(recurrente)} title="Editar">
                  <MdEdit />
                </RecurrentIconBtn>
                <RecurrentIconBtn onClick={() => handleEliminarRecurrente(recurrente)} title="Eliminar">
                  <MdDelete />
                </RecurrentIconBtn>
              </RecurrentActions>

              <RecurrentHeader>
                <MdRepeat />
                <div>
                  <h3>Cargo Recurrente</h3>
                  <RecurrentBadge>
                    {recurrente.habilitado ? (
                      <>
                        <MdCheckCircle /> Habilitado
                      </>
                    ) : (
                      <>
                        <MdCancel /> Deshabilitado
                      </>
                    )}
                  </RecurrentBadge>
                </div>
              </RecurrentHeader>

              <RecurrentInfo>
                <RecurrentInfoItem>
                  <RecurrentLabel>Frecuencia</RecurrentLabel>
                  <RecurrentValue>{recurrente.frecuencia}</RecurrentValue>
                  {recurrente.frecuencia === 'Semanal' && recurrente.dia_semana && (
                    <RecurrentBadge>Cada {recurrente.dia_semana}</RecurrentBadge>
                  )}
                  {recurrente.frecuencia === 'Mensual' && recurrente.dia_mes && (
                    <RecurrentBadge>Día {recurrente.dia_mes} de cada mes</RecurrentBadge>
                  )}
                  {recurrente.frecuencia === 'Anual' && recurrente.mes_anual && recurrente.dia_anual && (
                    <RecurrentBadge>
                      {new Date(2000, recurrente.mes_anual - 1, 1).toLocaleDateString('es-MX', { month: 'long' })} {recurrente.dia_anual}
                    </RecurrentBadge>
                  )}
                </RecurrentInfoItem>

                <RecurrentInfoItem>
                  <RecurrentLabel>Monto</RecurrentLabel>
                  <RecurrentValue>${parseFloat(recurrente.monto).toFixed(2)}</RecurrentValue>
                </RecurrentInfoItem>

                <RecurrentInfoItem>
                  <RecurrentLabel>Descripción</RecurrentLabel>
                  <RecurrentValue style={{ fontSize: '1rem' }}>{recurrente.descripcion}</RecurrentValue>
                </RecurrentInfoItem>

                <RecurrentInfoItem>
                  <RecurrentLabel>Próximo Cobro</RecurrentLabel>
                  <RecurrentValue style={{ fontSize: '1.25rem' }}>
                    <MdCalendarToday style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: '4px' }} />
                    {new Date(recurrente.proximo_cobro).toLocaleDateString('es-MX')}
                  </RecurrentValue>
                  {recurrente.ultimo_cobro && (
                    <RecurrentLabel style={{ marginTop: '4px' }}>
                      Último: {new Date(recurrente.ultimo_cobro).toLocaleDateString('es-MX')}
                    </RecurrentLabel>
                  )}
                </RecurrentInfoItem>
              </RecurrentInfo>
            </RecurrentCard>
          ))
        ) : (
          !recurrenteFormOpen && (
            <NoRecurrentCard>
              <MdRepeat />
              <p>Este cliente no tiene cargos recurrentes configurados</p>
            </NoRecurrentCard>
          )
        )}
      </div>

      <FullWidthCard>
        <CardTitle>Recibos del Cliente ({recibos.length})</CardTitle>
        
        {recibos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            <p>Este cliente no tiene recibos registrados</p>
          </div>
        ) : (
          <TableWrapper>
          <Table>
            <Thead>
              <tr>
                {client.total_subclientes > 0 && <Th>Cliente</Th>}
                <Th>Número</Th>
                <Th>Descripción</Th>
                <Th>Fecha Emisión</Th>
                <Th>Vencimiento</Th>
                <Th>Monto Total</Th>
                <Th>Saldo Pendiente</Th>
                <Th>Estado</Th>
                <Th>Acciones</Th>
              </tr>
            </Thead>
            <tbody>
              {recibos.map((recibo) => {
                const estadoBadge = getEstadoBadge(recibo.estado);
                return (
                  <tr key={recibo.id}>
                    {client.total_subclientes > 0 && (
                      <Td>{recibo.cliente_padre_id ? recibo.cliente_nombre : `${client.nombre} (propio)`}</Td>
                    )}
                    <Td>{recibo.numero_recibo}</Td>
                    <Td>{recibo.descripcion || 'Sin descripción'}</Td>
                    <Td>{new Date(recibo.fecha_emision).toLocaleDateString('es-MX')}</Td>
                    <Td>{new Date(recibo.fecha_vencimiento).toLocaleDateString('es-MX')}</Td>
                    <Td>${parseFloat(recibo.monto_total).toFixed(2)}</Td>
                    <Td style={{
                      color: parseFloat(recibo.saldo_pendiente) > 0 ? '#ef4444' : '#10b981',
                      fontWeight: '600'
                    }}>
                      ${parseFloat(recibo.saldo_pendiente).toFixed(2)}
                    </Td>
                    <Td>
                      <StatusBadge
                        status={recibo.estado}
                        style={{
                          backgroundColor: estadoBadge.color + '20',
                          color: estadoBadge.color
                        }}
                      >
                        {estadoBadge.text}
                      </StatusBadge>
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <ActionButton onClick={() => navigate(`/recibos/${recibo.id}`)}>
                          <MdVisibility />
                          Ver
                        </ActionButton>
                        {parseFloat(recibo.saldo_pendiente) > 0 && (
                          <ActionButton
                            onClick={() => navigate('/registrar-pago', { state: { reciboId: recibo.id } })}
                            style={{ backgroundColor: '#10b981', color: 'white' }}
                          >
                            <MdPayment />
                            Pagar
                          </ActionButton>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          </TableWrapper>
        )}
      </FullWidthCard>

      <FullWidthCard>
        <CardTitle>Acciones del Cliente</CardTitle>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>

          <Button
            onClick={handleDeleteClient}
            style={{ backgroundColor: '#fee2e2', color: '#dc2626', borderColor: '#fecaca' }}
          >
            <MdDelete />
            Eliminar Cliente
          </Button>
          <Button onClick={handleGeneratePDF}>
            Generar Estado de Cuenta PDF
          </Button>
          <Button onClick={handleSendEmail}>
            Enviar Estado de Cuenta por Email
          </Button>
        </div>
      </FullWidthCard>

      <TimelineSection>
        <TimelineHeader>
          <TimelineTitle>Actividad</TimelineTitle>
          <SmallButton primary onClick={() => setShowActividadForm(v => !v)}>
            + Registrar actividad
          </SmallButton>
        </TimelineHeader>

        {showActividadForm && (
          <ActivityForm>
            <SmallSelect
              value={nuevaActividad.tipo}
              onChange={e => setNuevaActividad(a => ({ ...a, tipo: e.target.value }))}
            >
              <option value="nota">Nota</option>
              <option value="llamada">Llamada</option>
              <option value="reunion">Reunión</option>
              <option value="email">Email</option>
            </SmallSelect>
            <SmallTextarea
              placeholder="Describe la actividad..."
              value={nuevaActividad.descripcion}
              onChange={e => setNuevaActividad(a => ({ ...a, descripcion: e.target.value }))}
            />
            <div>
              <SmallButton primary onClick={handleCrearActividad} disabled={savingActividad}>
                {savingActividad ? 'Guardando...' : 'Guardar'}
              </SmallButton>
              <SmallButton onClick={() => setShowActividadForm(false)}>Cancelar</SmallButton>
            </div>
          </ActivityForm>
        )}

        {loadingActividades ? (
          <p style={{ color: 'var(--color-text-light, #aaa)', fontSize: '0.875rem' }}>Cargando actividades...</p>
        ) : actividades.length === 0 ? (
          <p style={{ color: '#aaa', fontSize: '0.875rem', textAlign: 'center', padding: '24px 0' }}>
            Sin actividades registradas aún.
          </p>
        ) : (
          actividades.map(act => (
            <ActivityItem key={act.id}>
              <ActivityDot tipo={act.tipo} />
              <ActivityContent>
                <ActivityMeta>
                  <ActivityTipo>{act.tipo}</ActivityTipo>
                  {act.usuario_nombre && <ActivityTipo>— {act.usuario_nombre}</ActivityTipo>}
                  <ActivityFecha>{new Date(act.fecha).toLocaleString('es-MX')}</ActivityFecha>
                </ActivityMeta>
                <ActivityDesc>{act.descripcion}</ActivityDesc>
              </ActivityContent>
            </ActivityItem>
          ))
        )}
      </TimelineSection>

      <Adjuntos entidadTipo="cliente" entidadId={id} />
      <CalendarioRecordatorios entidadTipo="cliente" entidadId={id} />

    </ClientProfileContainer>
  );
};

export default ClientProfile;
